import {
  ButtonBase,
  darken,
  Stack,
  styled,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import shadows from "@mui/material/styles/shadows";
import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ROUTES } from "../../../../constants/routes";
import { walletEvents, WalletType } from "../../../../events/walletEvents";
import WalletTypeIcon from "../WalletTypeIcon";

const Item = styled(ButtonBase)(({ color, theme }) => ({
  cursor: "pointer",
  overflow: "hidden",
  backgroundColor: color,
  color: darken(color ?? theme.palette.pink[500], 0.1),
  borderRadius: 12,
  textAlign: "left",
  justifyContent: "flex-start",
  padding: theme.spacing(2),
  transition: theme.transitions.create("transform", {
    duration: theme.transitions.duration.short,
  }),
  "&:hover": {
    transform: "scale(1.05)",
    shadows: shadows[4],
  },
}));

interface CreateWalletTypeItemProps {
  icon: React.ReactNode;
  color: string;
  title: string;
  description: string;
  href?: string;
  type: WalletType;
  asLink?: boolean;
}

const CreateWalletTypeItem = ({
  icon,
  color,
  title,
  description,
  href,
  type,
  asLink = true,
}: CreateWalletTypeItemProps) => {
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    walletEvents.publish('wallet:type-selected', type);
  };

  const content = (
    <Item color={color} onClick={!asLink ? handleClick : undefined} sx={{ width: '100%' }}>
      <Stack direction="row" spacing={2} alignItems="center">
        {icon}
        <Stack>
          <Typography variant="h3" fontWeight={700} color="textPrimary">
            {title}
          </Typography>
          <Typography variant="body2" color="textPrimary">
            {description}
          </Typography>
        </Stack>
      </Stack>
    </Item>
  );

  if (!asLink) {
    return content;
  }

  return (
    <Link to={href ?? "#"} style={{ textDecoration: "none" }}>
      {content}
    </Link>
  );
};

interface CreateWalletTypeSelectorProps {
  renderFormOnPopup: boolean;
}

const CreateWalletTypeSelector = ({ renderFormOnPopup }: CreateWalletTypeSelectorProps) => {
  const theme: Theme = useTheme();
  const { t } = useTranslation('wallets');

  return (
    <Stack spacing={4}>
      <CreateWalletTypeItem
        color={theme.palette.pink[50]}
        title={t('type_selector.basic_title', "Ví cơ bản")}
        description={t('type_selector.basic_description', "Tiền mặt, tài khoản thanh toán hoặc tiết kiệm. Dùng để lập kế hoạch chi tiêu")}
        icon={<WalletTypeIcon type="basic" bgcolor={theme.palette.pink[500]} />}
        href={!renderFormOnPopup ? ROUTES.CREATE_BASIC_WALLET : undefined}
        type="basic"
        asLink={!renderFormOnPopup}
      />
      <CreateWalletTypeItem
        color={theme.palette.purple[50]}
        title={t('type_selector.credit_title', "Thẻ tín dụng")}
        description={t('type_selector.credit_description', "Thẻ tín dụng, hoặc các ví trả sau")}
        icon={
          <WalletTypeIcon type="credit" bgcolor={theme.palette.purple[500]} />
        }
        href={!renderFormOnPopup ? ROUTES.CREATE_CREDIT_WALLET : undefined}
        type="credit"
        asLink={!renderFormOnPopup}
      />
      <CreateWalletTypeItem
        color={theme.palette.teal[50]}
        title={t('type_selector.loan_title', "Ví vay nợ")}
        description={t('type_selector.loan_description', "Các khoản vay nợ cá nhân hoặc tổ chức")}
        icon={<WalletTypeIcon type="debit" bgcolor={theme.palette.teal[500]} />}
        href={!renderFormOnPopup ? ROUTES.CREATE_LOAN_WALLET : undefined}
        type="loan"
        asLink={!renderFormOnPopup}
      />
      {/* Hide uncompleted features
      <CreateWalletTypeItem
        color={theme.palette.navy[300]}
        title={t('type_selector.other_title', "Khác")}
        description={t('type_selector.other_description', "Các tài sản khác như chứng khoán, bảo hiểm,...")}
        icon={<WalletTypeIcon type="other" bgcolor={theme.palette.navy[600]} />}
        type="other"
      /> */}
    </Stack>
  );
};

export default CreateWalletTypeSelector;
